<template>
  <main-dialog
    v-model="show"
    :title="$t('trash.title')"
    size="xl"
    scrollable
    @hide="$emit('close')"
    hide-footer
  >
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-3 text-muted">{{ $t('trash.loading') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
      {{ error }}
    </div>

    <!-- Empty State -->
    <div v-else-if="!data || !data.items || data.items.length === 0" class="text-center py-5">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
      <h5 class="text-muted">{{ $t('trash.empty') }}</h5>
      <p class="text-muted">{{ $t('trash.emptyDescription') }}</p>
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Info Banner -->
      <b-alert show variant="info" class="mb-4">
        <b-icon icon="info-circle" class="mr-2"></b-icon>
        {{ $t('trash.infoMessage') }}
      </b-alert>

      <!-- Table -->
      <div class="table-responsive">
        <b-table
          :items="data.items"
          :fields="tableFields"
          striped
          hover
          :busy="restoring"
          class="deleted-models-table"
          show-empty
        >
          <template #cell(uuid)="data">
            <code class="text-primary">{{ data.value }}</code>
          </template>

          <template #cell(deleted_at)="data">
            <div>
              <div>{{ formatDate(data.value) }}</div>
              <small class="text-muted">{{ formatTimeAgo(data.value) }}</small>
            </div>
          </template>

          <template #cell(actions)="row">
            <div class="d-flex justify-content-end">
              <b-button
                variant="info"
                size="sm"
                @click="row.toggleDetails"
                class="mr-2"
              >
                <b-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'" class="mr-1"></b-icon>
                {{ row.detailsShowing ? $t('trash.hideDetails') : $t('trash.showDetails') }}
              </b-button>
              <b-button
                variant="success"
                size="sm"
                @click="handleRestore(row.item.uuid)"
                :disabled="restoring === row.item.uuid"
              >
                <b-spinner small v-if="restoring === row.item.uuid" class="mr-1"></b-spinner>
                <b-icon icon="arrow-counterclockwise" v-else class="mr-1"></b-icon>
                {{ $t('trash.restore') }}
              </b-button>
            </div>
          </template>

          <template #row-details="row">
            <b-card class="model-details-card">
              <h6 class="mb-3">{{ $t('trash.modelDetails') }}</h6>
              <pre class="model-json">{{ JSON.stringify(row.item, null, 2) }}</pre>
            </b-card>
          </template>
        </b-table>
      </div>

      <!-- Pagination -->
      <div class="pagination-controls">
        <div class="pagination-info">
          <span>{{ $t('trash.showing') }} {{ (page - 1) * limit + 1 }}-{{ Math.min(page * limit, data.total) }} {{ $t('trash.of') }} {{ data.total }}</span>
        </div>

        <div class="pagination-actions">
          <b-form-select
            v-model="limit"
            :options="limitOptions"
            size="sm"
            class="mr-3"
            style="width: auto;"
          ></b-form-select>

          <b-pagination
            v-model="page"
            :total-rows="data.total"
            :per-page="limit"
            size="sm"
            class="mb-0"
          ></b-pagination>
        </div>
      </div>
    </div>
  </main-dialog>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DeletedModels',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    componentId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      show: this.value,
      data: null,
      page: 1,
      limit: 50,
      loading: false,
      error: null,
      restoring: null,
      limitOptions: [
        { value: 25, text: '25 ' + this.$t('trash.perPage') },
        { value: 50, text: '50 ' + this.$t('trash.perPage') },
        { value: 100, text: '100 ' + this.$t('trash.perPage') }
      ]
    }
  },

  computed: {
    tableFields() {
      return [
        {
          key: 'uuid',
          label: this.$t('trash.uuid'),
          sortable: false
        },
        {
          key: 'deleted_at',
          label: this.$t('trash.deletedAt'),
          sortable: false
        },
        {
          key: 'actions',
          label: this.$t('trash.actions'),
          class: 'text-right'
        }
      ]
    }
  },

  watch: {
    value(val) {
      this.show = val
      if (val) {
        this.loadDeletedModels()
      }
    },
    show(val) {
      this.$emit('input', val)
    },
    page() {
      this.loadDeletedModels()
    },
    limit() {
      this.page = 1
      this.loadDeletedModels()
    }
  },

  mounted() {
    if (this.show) {
      this.loadDeletedModels()
    }
  },

  methods: {
    async loadDeletedModels() {
      this.loading = true
      this.error = null

      try {
        const response = await this.$http.get(`/v3/system/component/${this.componentId}/deleted`, {
          params: {
            page: this.page,
            limit: this.limit
          }
        })

        this.data = response.data
      } catch (err) {
        console.error('Failed to fetch deleted models:', err)
        this.error = (err.response && err.response.data && err.response.data.message) || this.$t('trash.fetchError')
      } finally {
        this.loading = false
      }
    },

    async handleRestore(modelUuid) {
      const confirmed = await this.$mainDialog.msgBoxConfirm(
        this.$t('trash.confirmRestore'),
        {
          title: this.$t('trash.confirmRestoreTitle'),
          okVariant: 'success',
          okTitle: this.$t('trash.restore'),
          cancelTitle: this.$t('cancel')
        }
      )

      if (!confirmed) return

      this.restoring = modelUuid

      try {
        await this.$http.post(`/v3/system/component/${this.componentId}/deleted/${modelUuid}/restore`)

        this.$bvToast.toast(this.$t('trash.restoreSuccess'), {
          title: this.$t('success'),
          variant: 'success'
        })

        // Refresh the list
        await this.loadDeletedModels()

        // Emit event to parent to refresh the main list
        this.$emit('restored')
      } catch (err) {
        console.error('Failed to restore model:', err)
        this.$bvToast.toast(
          (err.response && err.response.data && err.response.data.message) || this.$t('trash.restoreError'),
          {
            title: this.$t('error'),
            variant: 'danger'
          }
        )
      } finally {
        this.restoring = null
      }
    },

    formatDate(date) {
      return moment(date).format('YYYY-MM-DD HH:mm:ss')
    },

    formatTimeAgo(date) {
      return moment(date).fromNow()
    }
  }
}
</script>

<style scoped lang="scss">
.deleted-models-table {
  ::v-deep {
    th {
      border-top: none;
      background-color: var(--bg-card);
      color: var(--text-primary);
    }

    td {
      vertical-align: middle;
    }

    code {
      font-size: 0.875rem;
    }
  }
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-separator);

  .pagination-info {
    color: var(--text-secondary);
    font-size: 0.875rem;
  }

  .pagination-actions {
    display: flex;
    align-items: center;
  }
}

.model-details-card {
  background: var(--bg-hard2);
  border: 1px solid var(--border-separator);
  margin: 0.5rem 0;

  h6 {
    color: var(--text-primary);
    font-weight: 600;
  }
}

.model-json {
  background: var(--bg-card);
  border: 1px solid var(--border-separator);
  border-radius: 4px;
  padding: 1rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  max-height: 400px;
  overflow-y: auto;
  margin: 0;
}
</style>
